/**
 * `/settings/workspace` + `/settings/workspace/security` — RO-3-09 / #44302.
 *
 * Workspace General was a server component over `getWorkspace` + a FormData
 * identity form + a self-fetching `VocabularyEditor`. Security mounts two
 * self-fetching UI cards (`ChainStatusCard`, `RestorePickerCard`) with no page
 * data of its own.
 *
 * Transport:
 *   `getWorkspace()` → `useApi(userSettings.getWorkspace)`
 *   `saveWorkspaceIdentityForm` → `apiCall(userSettings.saveWorkspaceIdentityForm)`
 *   VocabularyEditor already self-fetches via its own contract.
 *
 * Live Next pages under `excellent/app/settings/workspace/**` are UNTOUCHED.
 */

import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import {
  ChainStatusCard,
  FormField,
  Input,
  RestorePickerCard,
  SettingsForm,
  SettingsPageShell,
  SettingsSection,
  VocabularyEditor,
} from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import { userSettings } from "@excellent/ui/api/contracts/user-settings";
import { rootRoute } from "./__root";

function WorkspaceGeneral() {
  const { data, isLoading, error } = useApi(userSettings.getWorkspace, {});
  const name = data?.name ?? "Workspace";

  async function onSave(fd: FormData): Promise<void> {
    await apiCall(userSettings.saveWorkspaceIdentityForm, {
      body: { workspace_name: String(fd.get("workspace_name") ?? "") },
    });
  }

  return (
    <SettingsPageShell
      title="Workspace"
      description="The workspace name and the language the app speaks."
    >
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : null}
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : (
        <SettingsForm action={onSave} saveLabel="Save name" key={name}>
          <SettingsSection
            id="identity"
            title="Identity"
            description="Shown in the avatar menu, breadcrumbs, and the browser tab title."
          >
            <FormField label="Workspace name" htmlFor="workspace_name">
              <Input
                id="workspace_name"
                name="workspace_name"
                defaultValue={name}
                className="max-w-md"
              />
            </FormField>
          </SettingsSection>
        </SettingsForm>
      )}
      <VocabularyEditor />
    </SettingsPageShell>
  );
}

function WorkspaceSecurity() {
  return (
    <SettingsPageShell
      title="Security"
      description="Encryption at rest, the tamper-evident audit chain, and point-in-time restore."
    >
      <ChainStatusCard />
      <RestorePickerCard />
    </SettingsPageShell>
  );
}

export const settingsWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace",
  component: WorkspaceGeneral,
});

export const settingsWorkspaceSecurityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/security",
  component: WorkspaceSecurity,
});
